'use client'

import React from 'react'
import { LazySvg } from '@/Components/custom/lazy-svg'
import { cn, formatUssdString } from '@/lib/utils'
import { Attribute, LanguageCode, Translatable } from '@/types'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/Components/ui/badge'

interface BundleRateDetailLineItemProps {
  field: LanguageCode | string | Translatable<string> | null | undefined
  label?: LanguageCode | string | Translatable<string> | null | undefined
  icon: string
  className?: string
}

export const BundleRateDetailLineItem: React.FC<BundleRateDetailLineItemProps> = ({
  field,
  label,
  icon,
  className,
}) => {
  const { i18n } = useTranslation()
  const getLocalizedValue = (
    value: LanguageCode | string | Translatable<string> | null | undefined
  ): string | null => {
    if (value === null || value === undefined || value === '') {
      return null
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'object') {
      const language = i18n.language as LanguageCode
      const localizedValue = value[language] || value['en']
      return localizedValue && localizedValue !== '' ? localizedValue : null
    }
    return null
  }

  const localizedField = getLocalizedValue(field)
  const localizedLabel = getLocalizedValue(label)

  if (localizedField === null) {
    return null
  }

  return (
    <li className="flex w-full items-center justify-between">
      <div className="flex items-center space-x-2 rtl:space-x-reverse">
        {icon && <LazySvg name={icon} containsPath={true} className="size-4 text-primary" />}

        <span className={cn('text-xs text-white', className)}>{localizedLabel}</span>
      </div>
      <div className="inline-flex items-center tabular-nums text-primary">
        <LazySvg name="/icons/currency-alt.svg" className="size-3.5" />
        <span className="text-sm font-medium text-white">{localizedField}</span>
      </div>
    </li>
  )
}

interface BundleDetailLineItemPlusProps {
  category: string
  attribute?: Attribute
  field: LanguageCode | string | Translatable<string> | null | undefined
  icon: string
  isUnlimited?: boolean
  label?: LanguageCode | string | Translatable<string> | null | undefined
  className?: string
  iconClassName?: string
}

export const BundleDetailLineItemPlus: React.FC<BundleDetailLineItemPlusProps> = ({
  attribute,
  category,
  field,
  icon,
  isUnlimited = false,
  label,
  className,
  iconClassName,
}) => {
  const { i18n } = useTranslation()
  const getLocalizedValue = (
    value: LanguageCode | string | Translatable<string> | null | undefined
  ): string | null => {
    if (value === null || value === undefined || value === '') {
      return null
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'object') {
      const language = i18n.language as LanguageCode
      const localizedValue = value[language] || value['en']
      return localizedValue && localizedValue !== '' ? localizedValue : null
    }
    return null
  }

  const localizedField = getLocalizedValue(field)
  const localizedLabel = getLocalizedValue(label)

  const ignoreableCategories = [
    'rate_note',
    'onnet_minutes_rate',
    'offnet_minutes_rate',
    'onnet_sms_rate',
    'offnet_sms_rate',
  ]
  const inArray = (value: string, array: string[]) => {
    return array.includes(value)
  }

  if (localizedField === null) {
    return null
  }

  if (inArray(category, ignoreableCategories)) {
    return null
  }

  if (typeof localizedField === 'string' && category === 'libraries') {
    const localizedFieldArray = localizedField.split(',').map((item) => item.trim())
    return (
      <li className="flex items-center space-x-2 rtl:space-x-reverse">
        {icon && (
          <LazySvg
            name={icon}
            containsPath={true}
            className={cn('size-4 text-primary', iconClassName)}
          />
        )}
        <div
          className={cn(
            'flex flex-wrap items-center gap-1 overflow-x-hidden text-sm rtl:space-x-reverse',
            label ? '' : ''
          )}
        >
          {attribute?.settings.name && <span>{getLocalizedValue(attribute?.settings.name)}:</span>}
          {localizedFieldArray.map((item, index) => (
            <Badge key={index} variant="primary-dark" className="">
              {item}
            </Badge>
          ))}
        </div>
      </li>
    )
  }

  if (category === 'ussd' || attribute?.category === 'ussd') {
    return (
      <li className="flex space-x-2 rtl:space-x-reverse">
        {icon && (
          <div className={cn(localizedField.length > 30 ? 'mt-2 self-start' : 'self-center')}>
            <LazySvg
              name={icon}
              containsPath={true}
              className={cn('size-4 text-primary', iconClassName)}
            />
          </div>
        )}
        <div
          className={cn('flex items-center text-sm', label ? 'space-x-1 rtl:space-x-reverse' : '')}
        >
          {label && <span>{localizedLabel}:</span>}
          <span className={cn('pfr leading-tight text-white', className)}>
            {formatUssdString(localizedField)}
          </span>
        </div>
      </li>
    )
  }

  return (
    <>
      {isUnlimited ? (
        <li className="flex w-full items-center justify-between">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {icon && (
              <LazySvg
                name={icon}
                containsPath={true}
                className={cn('size-4 text-primary', iconClassName)}
              />
            )}

            <div
              className={cn(
                'flex items-center text-sm',
                label ? 'space-x-1 rtl:space-x-reverse' : ''
              )}
            >
              {label && <span>{localizedLabel}:</span>}
              <span className={cn('text-white', className)}>{localizedField}</span>
            </div>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            className="size-6 text-primary"
            viewBox="0 0 22 11"
          >
            <path
              stroke="currentColor"
              strokeMiterlimit="10"
              strokeWidth="1.6"
              d="M2.30939 8.65889c1.7468 1.74681 4.57938 1.74681 6.32618 0l3.16263-3.16261-3.16263-3.16262C6.88877.58686 4.05619.58686 2.30939 2.3327c-1.74585 1.74776-1.74585 4.57939 0 6.32619ZM19.8836 8.1911c1.4885-1.48852 1.4885-3.90209 0-5.38965-1.4885-1.48851-3.9021-1.48851-5.3896 0l-2.6949 2.69483L14.494 8.1911c1.4885 1.48851 3.9021 1.48851 5.3896 0Z"
            />
          </svg>
        </li>
      ) : category === 'social_platforms' ? (
        <BundleDetailLineSocial />
      ) : (
        <li className="flex space-x-2 rtl:space-x-reverse">
          {icon && (
            <div className={cn(localizedField.length > 30 ? 'mt-2 self-start' : 'self-center')}>
              <LazySvg
                name={icon}
                containsPath={true}
                className={cn('size-4 text-primary', iconClassName)}
              />
            </div>
          )}
          <div
            className={cn(
              'flex items-center text-sm',
              label ? 'space-x-1 rtl:space-x-reverse' : ''
            )}
          >
            {label && <span>{localizedLabel}:</span>}
            <span className={cn('leading-tight text-white', className)}>{localizedField}</span>
          </div>
        </li>
      )}
    </>
  )
}

export const BundleDetailLineSocial: React.FC = () => (
  <li className="flex flex-col items-start space-y-1.5">
    <div className="flex space-x-1">
      <svg xmlns="http://www.w3.org/2000/svg" className="size-5" fill="none" viewBox="0 0 14 15">
        <path
          fill="#FEFC00"
          d="M7 14.2344c3.866 0 7-3.134 7-7.00002 0-3.866-3.134-7-7-7-3.86599 0-7 3.134-7 7 0 3.86602 3.13401 7.00002 7 7.00002Z"
        />
        <path
          fill="#050505"
          d="M7.00125 3.7798c1.07882 0 1.9847.89764 1.9847 1.9847 0 .2553 0 .48589.02471.7.00823.13177.12353.23059.24706.23059h.05764l.33765-.08235h.04118c.08235 0 .1647.05765.18117.14823.02471.12353-.04117.24706-.1647.28824l-.51059.20588c-.13177.04941-.19765.19765-.16471.32941.41177 1.52353 1.56474 1.36706 1.56474 1.56471 0 .24706-.87297.28823-.94709.36235-.07412.07412 0 .46118-.18118.53534-.03294.0164-.08235.0164-.14.0164-.13176 0-.32117-.0247-.51882-.0247-.17294 0-.34588.0165-.50235.0906-.42.1894-.8153.5518-1.31765.5518s-.89765-.3624-1.31765-.5518c-.15647-.0741-.33764-.0906-.50235-.0906-.19765 0-.38706.0247-.51882.0247-.05765 0-.10706 0-.14-.0164-.17294-.07416-.09883-.45299-.18118-.53534-.08235-.08235-.94706-.11529-.94706-.36235 0-.19765 1.15294-.04118 1.56471-1.56471.03294-.13176-.03294-.28-.16471-.32941l-.51059-.20588c-.11529-.04118-.18941-.16471-.1647-.28824.01647-.09058.09882-.14823.18117-.14823h.04118l.33765.08235h.05764c.12353 0 .23883-.09882.24706-.23059.01647-.21411.02471-.45294.02471-.7 0-1.08706.89765-1.9847 1.97647-1.9847m.02471-.42824c-.32118 0-.63412.06588-.93883.18941-.28823.12353-.54353.29647-.76588.51883-.22235.22235-.39529.47764-.51882.76588-.12353.29647-.18942.60941-.18942.93882 0 .15647 0 .31294-.00823.46118l-.12353-.03294c-.04941-.00824-.09059-.01647-.14-.01647-.14 0-.27176.04941-.37882.13176-.1153.09059-.18942.21412-.22236.35412-.07411.32941.10706.66706.42824.78235l.37882.15647c-.24706.76589-.70823.91412-1.02117 1.01294-.08236.02471-.15648.04942-.22236.08236-.27176.13176-.29647.36235-.29647.45294 0 .17294.08235.33765.23059.45294.07412.05765.16471.10706.28.14823.15647.05765.33765.09059.49412.1153 0 .05765.01647.11529.03294.17292.07412.2471.23059.3624.34588.4118.1153.0494.23059.0494.30471.0494.07411 0 .15647 0 .23882-.0082.09059 0 .18118-.0165.28-.0165.14 0 .24706.0165.32941.0494.10706.0494.21412.1153.32941.1812.32942.1894.7.4117 1.16118.4117.46118 0 .84-.2141 1.16118-.4117.11529-.0659.23059-.1318.32941-.1812.07412-.0329.18118-.0494.32941-.0494.09059 0 .18941 0 .28.0165.08235 0 .16471.0082.23882.0082.07412 0 .18942 0 .30471-.0494.11529-.0494.27176-.1565.34588-.4118.01651-.05763.02471-.11527.03291-.17292.1483-.02471.3294-.06588.4942-.1153.1152-.04117.2058-.09058.28-.14823.1482-.11529.2305-.27176.2305-.45294 0-.09059-.0329-.32118-.2964-.45294-.0659-.03294-.14-.05765-.2224-.08236-.3129-.09882-.7741-.24705-1.02116-1.01294l.37882-.15647c.32114-.11529.50234-.45294.42824-.78235-.0329-.14-.1071-.26353-.2224-.35412-.10701-.08235-.24701-.13176-.37878-.13176-.04941 0-.09882 0-.14.01647l-.12353.03294c0-.14824-.00823-.30471-.00823-.46118 0-.32117-.06589-.64235-.18941-.93882-.12353-.28824-.29648-.54353-.51883-.76588-.22235-.22236-.47765-.3953-.76588-.51883-.29647-.13176-.60941-.18941-.93882-.18941h-.01647Z"
        />
        <path
          fill="#fff"
          d="M7.00006 3.78125c1.07882 0 1.98471.89765 1.98471 1.98471 0 .25529 0 .48588.0247.7.00824.13176.12353.23058.24706.23058h.05765l.33764-.08235H9.693c.08235 0 .16471.05765.18118.14824.0247.12353-.04118.24706-.16471.28823l-.51059.20589c-.13176.04941-.19764.19764-.1647.32941.41176 1.52353 1.56472 1.36706 1.56472 1.5647 0 .24706-.87296.28824-.94708.36236-.07411.07411 0 .46117-.18117.53528-.03294.0165-.08235.0165-.14.0165-.13177 0-.32118-.0247-.51883-.0247-.17294 0-.34588.0164-.50235.0906-.42.1894-.81529.5517-1.31765.5517-.50235 0-.89764-.3623-1.31764-.5517-.15647-.0742-.33765-.0906-.50236-.0906-.19764 0-.38705.0247-.51882.0247-.05765 0-.10706 0-.14-.0165-.17294-.07411-.09882-.45293-.18118-.53528-.08235-.08236-.94705-.1153-.94705-.36236 0-.19764 1.15294-.04117 1.5647-1.5647.03294-.13177-.03294-.28-.1647-.32941l-.51059-.20589c-.1153-.04117-.18941-.1647-.16471-.28823.01647-.09059.09882-.14824.18118-.14824h.04117l.33765.08235h.05765c.12353 0 .23882-.09882.24706-.23058.01647-.21412.0247-.45295.0247-.7 0-1.08706.89765-1.98471 1.97647-1.98471"
        />
      </svg>

      <svg xmlns="http://www.w3.org/2000/svg" className="size-5" fill="none" viewBox="0 0 14 15">
        <path
          fill="url(#a)"
          d="M7 14.2344c3.866 0 7-3.134 7-7.00002 0-3.866-3.134-7-7-7-3.86599 0-7 3.134-7 7 0 3.86602 3.13401 7.00002 7 7.00002Z"
        />
        <path
          fill="#fff"
          d="m4.20774 8.28593-1.71294-.56s-.20588-.08235-.14-.27176c.01647-.04118.04118-.07413.12353-.13177.37882-.26353 7.00824-2.64353 7.00824-2.64353s.18941-.06588.29647-.02471c.05764.01647.09882.06589.10706.12353.00823.04942.01647.09883.01647.14824 0 .04118 0 .08235-.00824.14824-.04118.65058-1.25176 5.51763-1.25176 5.51763s-.07412.2882-.32942.2965c-.13176 0-.25529-.0412-.34588-.1318-.51059-.4365-2.2647-1.6141-2.65176-1.87763-.01647-.00824-.03294-.03294-.03294-.04941 0-.02471.0247-.05765.0247-.05765S8.3748 6.05416 8.44892 5.76593c0-.02471-.01647-.03294-.04941-.02471-.20589.07412-3.72236 2.29765-4.11765 2.54471-.02471.00823-.05765.00823-.08235 0h.00823Z"
        />
        <defs>
          <linearGradient
            id="a"
            x1="7"
            x2="7"
            y1="14.2426"
            y2=".23438"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#1D93D2" />
            <stop offset="1" stop-color="#38B0E3" />
          </linearGradient>
        </defs>
      </svg>

      <svg xmlns="http://www.w3.org/2000/svg" className="size-5" fill="none" viewBox="0 0 14 15">
        <path
          fill="url(#a)"
          d="M6.99177.23438C3.0517.23438 0 3.12486 0 7.02827c0 2.04229.83901 3.80463 2.19624 5.02333.11516.0989.18096.2471.18919.3953l.04113 1.2435c.00822.3953.4195.6588.78966.4941l1.39012-.6094c.11516-.0494.24678-.0576.37838-.0247.6416.173 1.3161.2718 2.02351.2718C10.9483 13.8222 14 10.9317 14 7.02827c0-3.9034-3.0599-6.7939-7-6.7939h-.00823Z"
        />
        <path
          fill="#fff"
          d="m2.78875 9.00893 2.0564-3.26107c.32903-.51881 1.0282-.65057 1.51351-.27999l1.6369 1.22702c.14806.11529.3537.11529.50999 0L10.71 5.01494c.2961-.22235.6827.13176.4771.44469L9.1307 8.7207c-.32903.51881-1.0282.65057-1.52174.27999l-1.6369-1.22702c-.14806-.11529-.36193-.11529-.50999 0L3.25761 9.45362c-.2879.22235-.6745-.13176-.47709-.44469h.00823Z"
        />
        <defs>
          <radialGradient
            id="a"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="matrix(15.1437 0 0 15.1635 2.51079 15.6589)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#09F" />
            <stop offset=".61" stop-color="#A033FF" />
            <stop offset=".93" stop-color="#FF5280" />
            <stop offset="1" stop-color="#FF7061" />
          </radialGradient>
        </defs>
      </svg>

      <svg xmlns="http://www.w3.org/2000/svg" className="size-5" fill="none" viewBox="0 0 14 15">
        <path
          fill="#75469D"
          d="M12.4331 1.59427C12.0706 1.27426 10.5794.25179 7.27568.24399c0 0-3.89688-.21854-5.8 1.42833C.42113 2.67137.05039 4.13873.0092 5.95732-.032 7.7759-.08143 11.1789 3.38704 12.1077v1.4128s-.02472.5697.37074.6868c.47784.1405.75795-.2888 1.21932-.7571.24715-.2576.60142-.6322.85681-.921 2.37273.1873 4.19349-.242 4.39939-.3044.4779-.1483 3.1884-.4761 3.625-3.87912.4532-3.5123-.2224-5.73675-1.4335-6.7358l.0083-.01561Zm.4037 6.48603c-.3708 2.8411-2.5705 3.0206-2.97419 3.1455-.17302.0546-1.77131.4292-3.78978.3044 0 0-1.49943 1.7171-1.96903 2.162-.15653.1483-.32131.1327-.32131-.1561 0-.1874.00824-2.3416.00824-2.3416-2.94119-.7727-2.76818-3.66836-2.73522-5.19035.03295-1.51419.33778-2.76301 1.22755-3.59815C3.8896 1.0245 7.20153 1.22743 7.20153 1.22743c2.79289.00781 4.13577.81173 4.44887 1.0771 1.0298.83515 1.5571 2.84106 1.1699 5.76797l.0165.0078ZM8.82454 5.87146c.00824.23415-.3625.24976-.37074.01561-.03295-.60099-.32954-.89758-.9392-.9288-.24716-.01561-.22245-.36684.01647-.35123.80739.03902 1.25228.47611 1.29347 1.26442Zm.58494.3044c.03296-1.16296-.73324-2.06835-2.18324-2.16982-.24716-.01561-.22244-.36684.02472-.35123 1.67244.11708 2.56221 1.20199 2.5375 2.52885 0 .23416-.37898.22635-.37074-.0078h-.00824Zm1.35942.36684c0 .23415-.3708.23415-.3708 0-.0165-2.22445-1.5818-3.44205-3.48492-3.45766-.24716 0-.24716-.35123 0-.35123 2.12556.01562 3.83922 1.40492 3.85572 3.80889Zm-.3296 2.68495c-.3131.52294-.898 1.10055-1.49118.91315-.60966-.17167-2.04318-.86632-2.94943-1.55317-.4696-.35122-.89802-.7649-1.22756-1.16296-.29659-.35122-.60142-.7727-.88977-1.27223-.6179-1.05368-.74972-1.52199-.74972-1.52199-.18949-.56977.41193-1.12393.96392-1.41272.26364-.13269.51904-.08586.69205.10927 0 0 .35426.40587.51079.60099.1483.18733.33779.48392.43665.64782.17301.2966.06591.601-.1071.72588l-.34603.26537c-.17301.13269-.15653.38245-.15653.38245s.5108 1.842 2.4304 2.30251c0 0 .26363.02341.40369-.1483l.28011-.32782c.13182-.1639.45313-.26537.7662-.10146.42017.22634.96392.57758 1.31821.89758.2059.15611.2471.39026.1071.64782l.0082.00781Z"
        />
      </svg>

      <svg xmlns="http://www.w3.org/2000/svg" className="size-5" fill="none" viewBox="0 0 14 15">
        <path
          fill="#000"
          d="M7 14.2344c3.866 0 7-3.134 7-7.00002 0-3.866-3.134-7-7-7-3.86599 0-7 3.134-7 7 0 3.86602 3.13401 7.00002 7 7.00002Z"
        />
        <path
          fill="#fff"
          d="M9.42085 3.4375h1.29295L7.88908 6.66574l3.31882 4.38936H8.60556L6.57144 8.39515 4.24085 11.0551H2.94791l3.02235-3.45054L2.7832 3.4375h2.66824l1.8447 2.43765L9.42909 3.4375h-.00824Zm-.45294 6.8435h.71647l-4.62-6.11056H4.2985L8.97614 10.281h-.00823Z"
        />
      </svg>

      <svg
        width="14"
        height="15"
        viewBox="0 0 14 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="size-5"
      >
        <path
          d="M3.92473 5.47003C3.92473 5.73861 3.71885 5.94844 3.46355 5.94844C3.20826 5.94844 2.99414 5.73861 2.99414 5.47003C2.99414 5.20144 3.20826 5 3.47179 5C3.71885 5 3.93296 5.21823 3.93296 5.47003H3.92473Z"
          fill="#2D58A1"
        />
        <path
          d="M3.02734 6.66391C3.02734 6.46247 3.02734 6.41211 3.08499 6.31139C3.14264 6.1855 3.29087 6.10156 3.44734 6.10156C3.60381 6.10156 3.74381 6.1855 3.80146 6.31139C3.85087 6.41211 3.85911 6.45408 3.85911 6.66391V8.54401C3.85911 8.74545 3.85911 8.79581 3.80146 8.89653C3.74381 9.02242 3.59558 9.10636 3.44734 9.10636C3.29911 9.10636 3.15087 9.02242 3.08499 8.89653C3.03558 8.79581 3.02734 8.75384 3.02734 8.54401V6.66391Z"
          fill="#2D58A1"
        />
        <path
          d="M4.28838 6.5823C4.28838 6.28854 4.45314 6.0871 4.70028 6.0871C4.86504 6.0871 4.95565 6.14585 5.10394 6.36408C5.3346 6.14585 5.54054 6.07031 5.83711 6.07031C6.04305 6.07031 6.249 6.12067 6.40552 6.213C6.4879 6.26336 6.54557 6.31372 6.63618 6.43962C6.97394 6.16264 7.19636 6.07031 7.51764 6.07031C8.10252 6.07031 8.52266 6.48998 8.52266 7.06072V8.54633C8.52266 8.74777 8.52266 8.79813 8.46499 8.89885C8.39909 9.02475 8.25081 9.11708 8.10252 9.11708C7.95424 9.11708 7.79772 9.03314 7.74006 8.89885C7.69063 8.79813 7.68239 8.75616 7.68239 8.54633V7.24537C7.68239 6.98518 7.5094 6.80053 7.25402 6.80053C6.99865 6.80053 6.81742 6.98518 6.81742 7.24537V8.54633C6.81742 8.74777 6.81742 8.79813 6.75975 8.89885C6.69385 9.02475 6.54557 9.11708 6.39728 9.11708C6.249 9.11708 6.09248 9.03314 6.03481 8.89885C5.98539 8.79813 5.97715 8.75616 5.97715 8.54633V7.32931C5.97715 6.99357 5.82063 6.80892 5.54054 6.80892C5.29341 6.80892 5.10394 6.99357 5.10394 7.25377V8.55472C5.10394 8.75616 5.10394 8.80652 5.04627 8.90724C4.98037 9.03314 4.83208 9.12547 4.6838 9.12547C4.53552 9.12547 4.379 9.04154 4.32134 8.90724C4.27191 8.80652 4.26367 8.76456 4.26367 8.55472V6.5907H4.28015L4.28838 6.5823Z"
          fill="#2D58A1"
        />
        <path
          d="M11.2475 6.54092C11.5029 6.8179 11.6429 7.20399 11.6429 7.63205C11.6429 8.52173 11.0086 9.1932 10.1684 9.1932C9.75647 9.1932 9.35282 9.01694 9.08097 8.71478C8.81736 8.42941 8.68555 8.05171 8.68555 7.60686C8.68555 6.74236 9.3281 6.0625 10.1684 6.0625C10.572 6.0625 10.9757 6.23876 11.2475 6.53253V6.54092ZM9.55876 7.62365C9.55876 8.0601 9.81414 8.38744 10.1519 8.38744C10.4896 8.38744 10.778 8.05171 10.778 7.63205C10.778 7.21238 10.5061 6.88504 10.1519 6.88504C9.82237 6.88504 9.55876 7.21238 9.55876 7.63205V7.62365Z"
          fill="#2D58A1"
        />
        <path
          d="M7.14608 14.2344C5.71269 14.2344 4.37816 13.7811 3.27428 13.0173C2.7553 13.2943 1.58552 13.3615 1.07477 13.2943C0.333362 13.202 -0.441001 12.5809 0.300408 11.9682C1.46195 11.0281 1.14891 10.6504 1.14891 10.6504C0.596974 9.64325 0.275695 8.47658 0.275695 7.23438C0.275695 3.37346 3.34843 0.234375 7.13785 0.234375C10.9273 0.234375 14 3.36507 14 7.23438C14 11.1037 10.9273 14.2344 7.12961 14.2344H7.14608ZM7.14608 1.26675C3.88388 1.26675 1.24777 3.96099 1.24777 7.28474C1.24777 8.42622 1.56081 9.50056 2.10451 10.407C2.10451 10.407 2.49168 11.431 1.50314 12.1864C2.12922 12.3962 3.43904 12.0102 3.50494 12.0102C4.50996 12.8159 5.7786 13.3027 7.15432 13.3027C10.4165 13.3027 13.0527 10.6085 13.0527 7.28474C13.0527 3.96099 10.4083 1.26675 7.15432 1.26675H7.14608Z"
          fill="#2D58A1"
        />
        <path
          d="M3.92473 5.47003C3.92473 5.73861 3.71885 5.94844 3.46355 5.94844C3.20826 5.94844 2.99414 5.73861 2.99414 5.47003C2.99414 5.20144 3.20826 5 3.47179 5C3.71885 5 3.93296 5.21823 3.93296 5.47003H3.92473Z"
          fill="#2D58A1"
        />
        <path
          d="M3.02734 6.66391C3.02734 6.46247 3.02734 6.41211 3.08499 6.31139C3.14264 6.1855 3.29087 6.10156 3.44734 6.10156C3.60381 6.10156 3.74381 6.1855 3.80146 6.31139C3.85087 6.41211 3.85911 6.45408 3.85911 6.66391V8.54401C3.85911 8.74545 3.85911 8.79581 3.80146 8.89653C3.74381 9.02242 3.59558 9.10636 3.44734 9.10636C3.29911 9.10636 3.15087 9.02242 3.08499 8.89653C3.03558 8.79581 3.02734 8.75384 3.02734 8.54401V6.66391Z"
          fill="#2D58A1"
        />
        <path
          d="M4.28838 6.5823C4.28838 6.28854 4.45314 6.0871 4.70028 6.0871C4.86504 6.0871 4.95565 6.14585 5.10394 6.36408C5.3346 6.14585 5.54054 6.07031 5.83711 6.07031C6.04305 6.07031 6.249 6.12067 6.40552 6.213C6.4879 6.26336 6.54557 6.31372 6.63618 6.43962C6.97394 6.16264 7.19636 6.07031 7.51764 6.07031C8.10252 6.07031 8.52266 6.48998 8.52266 7.06072V8.54633C8.52266 8.74777 8.52266 8.79813 8.46499 8.89885C8.39909 9.02475 8.25081 9.11708 8.10252 9.11708C7.95424 9.11708 7.79772 9.03314 7.74006 8.89885C7.69063 8.79813 7.68239 8.75616 7.68239 8.54633V7.24537C7.68239 6.98518 7.5094 6.80053 7.25402 6.80053C6.99865 6.80053 6.81742 6.98518 6.81742 7.24537V8.54633C6.81742 8.74777 6.81742 8.79813 6.75975 8.89885C6.69385 9.02475 6.54557 9.11708 6.39728 9.11708C6.249 9.11708 6.09248 9.03314 6.03481 8.89885C5.98539 8.79813 5.97715 8.75616 5.97715 8.54633V7.32931C5.97715 6.99357 5.82063 6.80892 5.54054 6.80892C5.29341 6.80892 5.10394 6.99357 5.10394 7.25377V8.55472C5.10394 8.75616 5.10394 8.80652 5.04627 8.90724C4.98037 9.03314 4.83208 9.12547 4.6838 9.12547C4.53552 9.12547 4.379 9.04154 4.32134 8.90724C4.27191 8.80652 4.26367 8.76456 4.26367 8.55472V6.5907H4.28015L4.28838 6.5823Z"
          fill="#2D58A1"
        />
        <path
          d="M11.2475 6.54092C11.5029 6.8179 11.6429 7.20399 11.6429 7.63205C11.6429 8.52173 11.0086 9.1932 10.1684 9.1932C9.75647 9.1932 9.35282 9.01694 9.08097 8.71478C8.81736 8.42941 8.68555 8.05171 8.68555 7.60686C8.68555 6.74236 9.3281 6.0625 10.1684 6.0625C10.572 6.0625 10.9757 6.23876 11.2475 6.53253V6.54092ZM9.55876 7.62365C9.55876 8.0601 9.81414 8.38744 10.1519 8.38744C10.4896 8.38744 10.778 8.05171 10.778 7.63205C10.778 7.21238 10.5061 6.88504 10.1519 6.88504C9.82237 6.88504 9.55876 7.21238 9.55876 7.63205V7.62365Z"
          fill="#2D58A1"
        />
        <path
          d="M7.14608 14.2344C5.71269 14.2344 4.37816 13.7811 3.27428 13.0173C2.7553 13.2943 1.58552 13.3615 1.07477 13.2943C0.333362 13.202 -0.441001 12.5809 0.300408 11.9682C1.46195 11.0281 1.14891 10.6504 1.14891 10.6504C0.596974 9.64325 0.275695 8.47658 0.275695 7.23438C0.275695 3.37346 3.34843 0.234375 7.13785 0.234375C10.9273 0.234375 14 3.36507 14 7.23438C14 11.1037 10.9273 14.2344 7.12961 14.2344H7.14608ZM7.14608 1.26675C3.88388 1.26675 1.24777 3.96099 1.24777 7.28474C1.24777 8.42622 1.56081 9.50056 2.10451 10.407C2.10451 10.407 2.49168 11.431 1.50314 12.1864C2.12922 12.3962 3.43904 12.0102 3.50494 12.0102C4.50996 12.8159 5.7786 13.3027 7.15432 13.3027C10.4165 13.3027 13.0527 10.6085 13.0527 7.28474C13.0527 3.96099 10.4083 1.26675 7.15432 1.26675H7.14608Z"
          fill="#2D58A1"
        />
      </svg>

      <svg xmlns="http://www.w3.org/2000/svg" className="size-5" fill="none" viewBox="0 0 14 15">
        <path
          fill="url(#a)"
          d="M13.9668 4.34892c-.0329-.74892-.1563-1.25918-.3208-1.70359-.181-.46088-.4196-.84768-.8062-1.23449-.3866-.38681-.7814-.6337-1.2339-.80654-.4442-.17282-.9542-.28804-1.70273-.32096-.74856-.03292-.98711-.04115-2.8873-.04115-1.90018 0-2.13873.00823-2.88729.04115C3.38002.31626 2.87002.4397 2.42582.6043c-.46065.18105-.8555.41972-1.24212.80653-.38661.38681-.6334.78184-.80614 1.23449-.17274.44441-.2879.95467-.3208 1.70359-.03291.74893-.04113.9876-.04113 2.88871 0 1.90112.00822 2.13978.04112 2.88867.0329.749.1563 1.2592.32081 1.7036.18097.4609.41953.8477.80614 1.2345.38662.3868.78146.6337 1.23389.8066.4442.1728.9542.288 1.70276.3209.74856.0329.98711.0412 2.8873.0412 1.90019 0 2.13873-.0083 2.88729-.0412.74856-.0329 1.25856-.1563 1.70276-.3209.4607-.1811.8473-.4198 1.2339-.8066.3866-.3868.6334-.7818.8061-1.2345.1728-.4444.2879-.9546.3208-1.7036.0329-.74889.0412-.98755.0412-2.88867 0-1.90111 0-2.13978-.0412-2.88871h.0083Zm-1.2586 5.71158c-.0329.6831-.1481 1.0534-.2385 1.3003-.1234.321-.2797.5597-.5265.8066-.2468.2469-.4771.395-.8061.5267-.2468.0987-.617.214-1.29974.2386-.74033.033-.9542.0412-2.82971.0412-1.87551 0-2.08938-.0082-2.82972-.0412-.68275-.0329-1.05291-.1481-1.29969-.2386-.32081-.1235-.55936-.2798-.80614-.5267-.24677-.2469-.39484-.4774-.52646-.8066-.09871-.2469-.21387-.6172-.23855-1.3003-.0329-.7407-.04113-.95467-.04113-2.8311 0-1.87642.00823-2.0904.04113-2.8311.03291-.68308.14807-1.05343.23855-1.30033.12339-.32096.27969-.55963.52646-.80653.24678-.2469.4771-.39504.80614-.52671.24678-.09876.61694-.21398 1.29969-.23867.74034-.03292.95421-.04115 2.82972-.04115s2.08938.00823 2.82971.04115c.68274.03292 1.05294.14814 1.29974.23867.3208.12344.5593.27981.8061.52671.2468.2469.3948.47734.5265.80653.0987.2469.2138.61725.2385 1.30033.0329.7407.0411.95468.0411 2.8311 0 1.87643-.0082 2.0904-.0411 2.8311Z"
        />
        <path
          fill="url(#b)"
          d="M7.00879 3.63281c-1.99067 0-3.59473 1.61307-3.59473 3.59648 0 1.98342 1.61228 3.59651 3.59473 3.59651 1.98244 0 3.59471-1.61309 3.59471-3.59651 0-1.98341-1.60404-3.59648-3.59471-3.59648Zm0 5.93379c-1.29147 0-2.33616-1.04521-2.33616-2.33731s1.04469-2.3373 2.33616-2.3373c1.29147 0 2.33615 1.0452 2.33615 2.3373S8.30026 9.5666 7.00879 9.5666Z"
        />
        <path
          fill="#BC30A0"
          d="M10.7433 4.32734c.4634 0 .8391-.37583.8391-.83945 0-.46362-.3757-.83945-.8391-.83945-.4633 0-.839.37583-.839.83945 0 .46362.3757.83945.839.83945Z"
        />
        <path
          fill="url(#c)"
          d="M.00823 7.22982c0 1.90111.00823 2.13978.04113 2.88868.0329.7489.1563 1.2592.32081 1.7036.18097.4609.41952.8477.80614 1.2345.38661.3868.78146.6337 1.23388.8065.4442.1729.95421.2881 1.70277.321.74855.0329.98711.0412 2.88729.0412 1.90019 0 2.13874-.0083 2.8873-.0412.74855-.0329 1.25855-.1564 1.70275-.321.4607-.181.8473-.4197 1.2339-.8065.3866-.3868.6334-.7818.8061-1.2345.1728-.4444.2879-.9547.3208-1.7036.0329-.7489.0412-.98757.0412-2.88868 0-1.90112-.0083-2.13978-.0412-2.88871-.0329-.74892-.1562-1.25918-.3208-1.70359-.1809-.46088-.4195-.84769-.8061-1.23449-.3866-.38681-.7732-.6337-1.2339-.80654-.4442-.17283-.9542-.28805-1.70275-.32097C9.13899.2426 8.90044.23438 7.00025.23438c-1.90018 0-2.13874.00822-2.88729.04114C3.3644.30844 2.85439.4319 2.41019.5965c-.46065.18106-.84727.41973-1.23388.80654-.38662.3868-.6334.78184-.80614 1.23449-.17274.44441-.29613.95467-.32904 1.70359C.00823 5.09004 0 5.32047 0 7.22982h.00823Zm1.26679 0c0-1.8682.00822-2.0904.04112-2.8311.03291-.68308.14807-1.05343.23856-1.30033.12339-.32097.27968-.55963.52645-.80653.24678-.2469.47711-.39504.80615-.52672.24677-.09876.61694-.21398 1.29969-.23867.74033-.03292.9542-.04114 2.82971-.04114 1.87551 0 2.08938.00822 2.82972.04114.68278.03292 1.05288.14814 1.29968.23867.3208.12345.5594.27982.8061.52672.2468.2469.3949.47733.5265.80653.0987.2469.2139.61725.2386 1.30033.0329.7407.0411.95467.0411 2.8311 0 1.87642-.0082 2.0904-.0411 2.83108-.0329.6831-.1481 1.0534-.2386 1.3003-.1234.321-.2797.5597-.5265.8066-.2467.2469-.4771.395-.8061.5267-.2468.0988-.6169.214-1.29968.2387-.74034.0329-.95421.0411-2.82972.0411s-2.08938-.0082-2.82971-.0411c-.68275-.033-1.05292-.1482-1.29969-.2387-.32081-.1235-.55937-.2798-.80615-.5267-.24677-.2469-.39484-.4774-.52645-.8066-.09871-.2469-.21388-.6172-.23856-1.3003-.0329-.74068-.04112-.96289-.04112-2.83108Z"
        />
        <defs>
          <linearGradient
            id="a"
            x1="11.8445"
            x2="1.72067"
            y1="1.30385"
            y2="13.7097"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#AE3DAE" />
            <stop offset=".05" stop-color="#B23BA6" />
            <stop offset=".12" stop-color="#BD368E" />
            <stop offset=".21" stop-color="#CE2E69" />
            <stop offset=".32" stop-color="#E62335" />
            <stop offset=".42" stop-color="#FF1800" />
          </linearGradient>
          <linearGradient
            id="b"
            x1="8.728"
            x2="5.04013"
            y1="4.34882"
            y2="10.5279"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#E12F6A" />
            <stop offset=".17" stop-color="#EA3751" />
            <stop offset=".36" stop-color="#F13D3E" />
            <stop offset=".55" stop-color="#F64133" />
            <stop offset=".75" stop-color="#F7422F" />
            <stop offset=".79" stop-color="#F74C2F" />
            <stop offset=".87" stop-color="#F7652F" />
            <stop offset=".98" stop-color="#F78F2E" />
            <stop offset="1" stop-color="#F79A2E" />
          </linearGradient>
          <linearGradient
            id="c"
            x1="10.1755"
            x2="3.98442"
            y1="-.34172"
            y2="14.4289"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset=".23" stop-color="#9E35A5" stop-opacity="0" />
            <stop offset=".45" stop-color="#D42F7F" stop-opacity=".5" />
            <stop offset=".75" stop-color="#F7772E" />
            <stop offset=".96" stop-color="#FEF780" />
          </linearGradient>
        </defs>
      </svg>

      <svg xmlns="http://www.w3.org/2000/svg" className="size-5" fill="none" viewBox="0 0 14 15">
        <path
          fill="url(#a)"
          d="M5.84706 14.1487C2.52 13.5566 0 10.6865 0 7.22442 0 3.37578 3.15412.23437 7 .23437c3.8459 0 7 3.14964 7 6.99005 0 3.46208-2.52 6.33218-5.84706 6.92428l-.38706-.3125h-1.54l-.38706.3125h.00824Z"
        />
        <path
          fill="#fff"
          d="m9.7328 9.194.3129-1.95721H8.19281V5.87167c0-.5592.21411-.9786 1.05411-.9786h.91408V3.10855c-.49408-.07401-1.05407-.1398-1.53995-.1398-1.61412 0-2.73413.97861-2.73413 2.73023v1.53781H4.13281V9.194h1.75411v4.9341c.38706.0741.77413.107 1.15295.107.37882 0 .77411-.0329 1.15294-.107V9.194H9.7328Z"
        />
        <defs>
          <linearGradient
            id="a"
            x1="7.04941"
            x2="7.04941"
            y1="14.0159"
            y2=".60345"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#0062E0" />
            <stop offset="1" stop-color="#19AFFF" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  </li>
)

interface BundleDetailLineNoteProps {
  note: string | null | undefined
}
export const BundleDetailLineNote: React.FC<BundleDetailLineNoteProps> = ({ note }) => {
  if (note === null) {
    return null
  }

  return (
    <li className="flex flex-col items-start space-y-1.5">
      <div className="flex items-center justify-start space-x-2">
        <span className="self-start py-0.5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            className="-m-[1.5px] size-5"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
            />
          </svg>
        </span>
        <span className={cn('text-sm font-medium text-white')}>{note}</span>
      </div>
    </li>
  )
}
